import { Component, Vue, Prop } from "vue-facing-decorator";
import type { IComponentStyle } from "../types";
import { CAROUSEL_RADIUS } from "../constant";

@Component
export default class ContainerTag extends Vue {
	@Prop({ type: Object, default: () => ({}) })
	tagStyle?: IComponentStyle;

	@Prop({
		type: String,
		default: "",
	})
	@Prop()
	text: string;

	/**
	 * JIRA: SD-7490
	 * Computes and returns a style object for a tag element based on the provided `tagStyle` configuration.
	 *
	 * This method applies default styles and overrides them with any custom values from `tagStyle`.
	 * It also handles positioning logic depending on the tag's orientation and position type.
	 *
	 * @returns {Record<string, string | number | undefined>} A record of CSS styles to be applied to the tag.
	 *
	 * ## Default Behavior:
	 * - `fontSize`: "10px"
	 * - `fontWeight`: "500"
	 * - `color`: "#FFF"
	 * - `backgroundColor`: "#9E5AD4" (if not explicitly set)
	 * - `padding`: "2px 8px"
	 * - `borderRadius`: value from `CAROUSEL_RADIUS`
	 * - `width`/`height`: "fit-content"
	 * - `position`: determined by `tagOrientation` and `position`
	 * - `alignSelf`: determined by `position` if not absolutely positioned
	 *
	 * ## Positioning:
	 * - If `tagOrientation` is not "content" or "button", position is set to `absolute`
	 * - Defaults to top-left if no specific position is defined
	 *
	 * ## Max Line Clamping:
	 * - Uses both `WebkitLineClamp` and `lineClamp` for compatibility
	 */
	get styleTag(): Record<string, string | number | undefined> {
		let style: IComponentStyle = {};
		if (this.tagStyle && typeof this.tagStyle === "object" && !Array.isArray(this.tagStyle)) style = this.tagStyle;

		const {
			textAlign,
			fontSize = "10px",
			fontWeight = "500",
			color = "#FFF",
			background,
			margin,
			padding = "2px 8px",
			borderRadius = CAROUSEL_RADIUS,
			width = "fit-content",
			maxWidth,
			height = "fit-content",
			maxHeight,
			position,
			tagOrientation,
		} = style;

		let { backgroundColor, top, bottom, left, right } = style;

		if (!background && !backgroundColor) {
			backgroundColor = "#9E5AD4";
		}

		const isAbsolute = tagOrientation !== "content" && tagOrientation !== "button";

		let alignSelf = "auto";

		if (isAbsolute) {
			switch (position) {
				case "top-right":
					top = top || "0";
					right = right || "0";
					break;

				case "bottom-left":
					bottom = bottom || "0";
					left = left || "0";
					break;

				case "bottom-right":
					bottom = bottom || "0";
					right = right || "0";
					break;

				default:
					top = top || "0";
					left = left || "0";
					break;
			}
		} else {
			if (position === "bottom-right" || position === "top-right") {
				alignSelf = "flex-end";
			} else {
				alignSelf = "flex-start";
			}
		}

		return {
			position: isAbsolute ? "absolute" : "relative",
			textAlign,
			fontSize,
			fontWeight,
			color,
			background: background || backgroundColor,
			margin,
			padding,
			borderRadius,
			width,
			maxWidth,
			height,
			maxHeight,
			top,
			bottom,
			left,
			right,
			alignSelf,
		};
	}

	/**
	 * JIRA: SD-7490
	 * Returns a style object related to text line clamping for the tag element.
	 *
	 * This method extracts the `maxLines` value from `tagStyle` if available, defaulting to 1,
	 * and applies it to line clamping properties used for truncating multiline text.
	 *
	 * @returns {Record<string, string | number | undefined>} An object containing line clamping style properties.
	 *
	 * ## Included Styles:
	 * - `maxLines`: The maximum number of text lines to display (default is `1`)
	 * - `WebkitLineClamp`: For WebKit-based browsers (e.g., Chrome, Safari)
	 * - `lineClamp`: Standard property used for multiline text truncation
	 */
	get styleTagText(): Record<string, string | number | undefined> {
		let maxLines = 1;

		if (this.tagStyle && this.tagStyle.maxLines) {
			maxLines = this.tagStyle.maxLines;
		}

		return {
			maxLines,
			WebkitLineClamp: maxLines,
			lineClamp: maxLines,
		};
	}
}
